import { useState, useContext, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import dynamic from "next/dynamic";
import { GlobalContext } from "../../stores/use_global_state";
import Layout from "../../components/layout";
import style from "./remove_video_bg.module.scss";
import { address, recoveryJob, getTaskStatus, getAccountData, getTrimmedVideoJob } from "../api/api.js";
import * as ga from "../../lib/ga";
import { videoFileExtensionList, photoFileExtensionList, fileExtensionList } from "../../constants/file_extension";
import getCommonLocales from "../../locales/locales_common";
import getErrorLocales from "../../locales/locales_error";
import getRemoveBgPageLocales from "../../locales/locales_remove_bg";
import OpenGraphTwitterCard from "../../components/seo/OpenGraphTwitterCard";
import UploadFileLayout from "../../components/remove_video_bg/UploadFileLanding";
import { gaCreditsLimit } from "../../utils/gaCreditsLimit";
import { removeFromLocalStorage } from "../../utils/nonAuthLocalStorage";
const DynamicTrimVideo = dynamic(() => import("../../components/remove_video_bg/TrimVideo/TrimVideo"), { ssr: false });
const DynamicVideoProcessing = dynamic(() => import("../../components/remove_video_bg/videoProcessing"), { ssr: false });
const DynamicAuthModal = dynamic(() => import("../../components/AuthModal/AuthModal"), { ssr: false });
const DynamicExtensionModal = dynamic(() => import("../../components/modal_window/modalWindow").then((res) => res.ExtensionModal), { ssr: false });

export default function RemoveVideoBackground({ cookies }) {
	const router = useRouter();
	const { locale, pathname } = router;
	const pageName = pathname.split("/").pop();
	const ourRequest = useRef(null);
	const [removePlanTarget, setRemovePlanTarget] = useState(null);
	const [uploadedProgress, setUploadedProgress] = useState(null); // null | number
	const [trimData, setTrimData] = useState({});
	const [extensionModal, setExtensionModal] = useState(false);
	const [isTrim, setIsTrim] = useState("pending"); // wip, done, pending
	const [showAuthModal, setShowAuthModal] = useState(false);

	const { dispatch, state } = useContext(GlobalContext);

	const openAuthModal = () => {
		setShowAuthModal(true);
	};
	const closeAuthModal = () => {
		setShowAuthModal(false);
	};

	const dictionary = { ...getCommonLocales(locale), ...getErrorLocales(locale), ...getRemoveBgPageLocales(locale) };

	const wrongExtensionHandler = () => {
		setExtensionModal(true);
		delFile();
		setIsTrim("pending");
	};

	const taskTimeout = useRef(null);

	const gaUploadFile = () => {
		ga.event({ action: `Bgremover page: click 'upload video'`, params: { event_category: "BG Remover" } });
	};

	// первичная проверка файла перед отправкой
	const uploadFile = (files) => {
		if (state.isAuth) {
			if (state?.accountData?.plan === "free" && state?.accountData?.total_videos < 1) {
				gaCreditsLimit("BGREM");
				dispatch({ type: "setVideoError", error_msg: "free_limit_video", error_code: "free_limit_video" });
				return false;
			}
			if (state?.accountData?.plan !== "free" && state?.accountData?.total_credits < 1) {
				gaCreditsLimit("BGREM");
				dispatch({ type: "setVideoError", error_msg: "pro_limit", error_code: "pro_limit" });
				return false;
			}
		}
		gaUploadFile();
		removeFromLocalStorage();
		sessionStorage.removeItem("searchParams");
		setIsTrim("pending");
		let video_file = files[0];
		let plan = "";
		const ExtensionList = new Set(fileExtensionList);

		let fileSize = video_file.size;
		let fileExtension = video_file.name.split(".").pop();

		ga.event({ action: `Bgremover page: file size: ${plan}`, params: { event_category: "files_uploaded_by_users", event_label: `${fileSize / (1024 * 1024)}mb` } });

		ga.event({ action: `Bgremover page: file extension: ${plan}`, params: { event_category: "files_uploaded_by_users", event_label: fileExtension } });

		if (!ExtensionList.has(fileExtension.toLowerCase())) {
			if (fileExtension === "heif") {
				wrongExtensionHandler();
				return;
			} else {
				dispatch({
					type: "setVideoError",
					error_msg: dictionary["Extension .XX is not allowed. Only .mp4, .mov, .ts, .avi, .mkv, .webm, .mpg, .jpg, .png are allowed"].replace("XX", fileExtension),
					error_code: "extension",
				});
				return false;
			}
		}

		dispatch({ type: "setVideo", video: video_file, grecaptchaToken: null });
	};

	// проверка файла на необходимость запуска функции обрезки видео
	useEffect(
		() => {
			if (state.video) {
				const videoExtensionList = new Set(videoFileExtensionList);
				const fileExtension = state.video.name.split(".").pop();

				if (fileExtension === "heif" || fileExtension === "heic") {
					wrongExtensionHandler();
					return;
				}

				if (videoExtensionList.has(fileExtension.toLowerCase())) {
					setIsTrim("wip");
					ga.event({ action: `User uploaded video`, params: { event_category: "BG Remover" } });
					return;
				}

				setIsTrim("done");
			}
		}, // eslint-disable-next-line react-hooks/exhaustive-deps
		[state.video]
	);

	const trimReadyHandler = (data) => {
		setTrimData(data);
		setIsTrim("done");
		ga.event({ action: `User trimmed video`, params: { event_category: "BG Remover" } });
	};

	const sessionRecovery = async () => {
		sessionStorage.removeItem("searchParams");

		if (router?.query?.purchased) {
			ga.event({ action: "pay_as_go" });
		}

		if (router?.query?.job_id && router?.query?.task_id) {
			let responseTask = await getTaskStatus(router?.query?.task_id);
			let responseTaskData = responseTask.data;
			let responseJob = await recoveryJob(state?.userInfo?.token, router?.query?.job_id);
			let saveBgData;

			try {
				saveBgData = JSON.parse(localStorage.activeGroup);
			} catch (error) {
				saveBgData = null;
			}

			let dispatchData = { previewImg: responseTaskData.preview.result_url, job: responseJob.data, editorData: saveBgData?.editorData };

			if (responseTaskData.status === "created") {
				dispatch({ ...dispatchData, type: "setRecoveryCheckout", checkout: responseTaskData, option: saveBgData.option });
			} else {
				dispatch({ ...dispatchData, type: "setRecoveryJob", task: responseTaskData, option: { activeGroup: responseTaskData.bg_group, itemActive: { file_url: responseTaskData.bg_url } } });
			}
			setRemovePlanTarget("pro");
			router.push({ query: {} });
			return;
		}
	};

	useEffect(
		() => {
			sessionStorage.removeItem("searchParams");
			const { params, job_id } = router.query;
			if (state?.load && router.isReady) {
				if (process.env.NEXT_PUBLIC_IS_CHINESE_VERSION === "true" && params === "pro") {
					state.isAuth && router.replace({ query: { ...router.query, params: "pro" } });
					return;
				}
				if (job_id) {
					sessionRecovery();
				} else if (params) {
					setRemovePlanTarget(params);
				} else if (state?.accountData?.plan) {
					router.replace({ query: { ...router.query, params: state?.accountData?.plan } });
				}
			}
		}, // eslint-disable-next-line react-hooks/exhaustive-deps
		[state?.load, router.query, state?.accountData?.plan]
	);

	// отправка файла на сервер
	const createJob = async () => {
		const { video, isAuth, grecaptchaToken, userInfo } = state;
		const { trim_start, trim_duration } = trimData;
		ourRequest.current = axios.CancelToken.source();

		setUploadedProgress(0);

		if (removePlanTarget === "free" && video && !isAuth) {
			let fileExtension = state.video.name.split(".").pop();
			let fileType = getFileType(fileExtension);
			let msg = null;

			const freeLimit = JSON.parse(localStorage.getItem("freeLimit"));

			ga.event({ action: `Bgremover page: file type`, params: { event_category: "files_uploaded_by_users", event_label: fileType } });

			if (freeLimit !== null && freeLimit.backgroundRemoving < 1) msg = dictionary["You have reached monthly limit."];

			if (msg) {
				dispatch({ type: "setVideoError", error_msg: msg, error_code: "free_limit_img" });
				return false;
			}
		}

		if (state?.video) {
			const config = {
				onUploadProgress: function (progressEvent) {
					let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
					setUploadedProgress(percentCompleted);
				},
				headers: {},
				cancelToken: ourRequest.current.token,
				validateStatus: function (status) {
					return status;
				},
			};
			if (isAuth) {
				config.headers = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${userInfo.token}` };
			} else {
				config.headers = { "Content-Type": "multipart/form-data" };
			}
			let formData = new FormData();
			formData.append("file", video);
			formData.append("grecaptchaToken", grecaptchaToken);
			formData.append("trim_start", trim_start);
			formData.append("trim_duration", trim_duration);
			try {
				let response = await axios.post(`${address}/jobs/`, formData, config);
				if (199 < response.status && response.status < 300) {
					dispatch({ type: "setJob", job: response.data });
				} else if (499 < response.status) {
					dispatch({ type: "setVideoError", error_msg: dictionary["500 | Server Error"], error_code: "text" });
				} else {
					let msg = response.data.message;
					dispatch({ type: "setVideoError", error_msg: msg, error_code: "text" });
					return false;
				}
				return () => {
					ourRequest.current.cancel();
					dispatch({ type: "setVideoDel" });
				};
			} catch (error) {
				dispatch({ type: "setVideoError", error_msg: dictionary["500 | Server Error"], error_code: "text" });
			}
		}
	};

	// инициализация отправки файла если все предварительные проверки и обрезка видео закончены
	useEffect(
		() => {
			if (isTrim === "done") createJob();
		}, // eslint-disable-next-line react-hooks/exhaustive-deps
		[isTrim]
	);

	useEffect(
		() => {
			state.task === null && clearTimeout(taskTimeout.current);

			const updateTaskData = async () => {
				if (state?.task?.status === "error") {
					let errorMsg = state.job.media_type === "video" ? dictionary["An error occurred while processing the video"] : dictionary["An error occurred while processing the photo"];
					dispatch({ type: "setVideoError", error_msg: errorMsg, error_code: "text" });
					dispatch({ type: "setVideoDel" });
					return false;
				}

				if (state?.task && state.task.status !== "done") {
					let { data } = await getTaskStatus(state.task.id);
					taskTimeout.current = setTimeout(() => {
						dispatch({ type: "setTask", action: "update", task: { ...data } });
					}, 2000);
				}

				if (state?.task && state.task.status === "done") {
					if (state.task.output_type === "preview") {
						const interval = setInterval(async () => {
							let { data } = await getTrimmedVideoJob(state.job.id);
							if (data?.status === "READY") {
								dispatch({ type: "setTrimmedFileInfo", job: { ...data, status: "previewDone" }, previewImg: state.task.result_url });
								clearInterval(interval);
								return;
							}
							if (data?.status === "FAILED") {
								let errorMsg = state.job.media_type === "video" ? dictionary["An error occurred while processing the video"] : dictionary["An error occurred while processing the photo"];
								dispatch({ type: "setVideoError", error_msg: errorMsg, error_code: "text" });
								dispatch({ type: "setVideoDel" });
								clearInterval(interval);
								return false;
							}
						}, 1000);
					}

					if (state.task.output_type === "video") {
						dispatch({ type: "setResultURL", resultURL: state.task, downloadURL: state.task.download_url });

						if (state?.userInfo) {
							let { data } = await getAccountData(state.userInfo.token);
							let restrictions = state.restrictions.map((item) => {
								item.active = item.status === data.plan;
								return item;
							});

							dispatch({ type: "setAccountData", accountData: data, restrictions: restrictions });
						}
					}
				}
			};

			updateTaskData();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[state.task]
	);

	useEffect(() => {
		return () => {
			if (taskTimeout.current !== null) clearTimeout(taskTimeout.current);
		};
	}, []);

	const saveToLocalStorage = (add, prompt = "", lang = "") => {
		const { job, task, option, previewImg, editorData } = state;
		let data = { job, task, option, previewImg, editorData, type: pageName, magicBgPrompt: prompt || null, magicBgLang: lang || null };
		add ? localStorage.setItem("activeGroup", JSON.stringify(data)) : localStorage.removeItem("activeGroup");
	};

	const delFile = () => {
		if (ourRequest.current) ourRequest.current.cancel();
		dispatch({ type: "setVideoDel" });
		saveToLocalStorage(false);
		sessionStorage.removeItem("searchParams");
	};

	const getFileType = (fileExtension) => {
		const videoExtensionList = new Set(videoFileExtensionList);

		const photoExtensionList = new Set(photoFileExtensionList);

		if (photoExtensionList.has(fileExtension.toLowerCase())) return "image";

		if (videoExtensionList.has(fileExtension.toLowerCase())) return "video";
	};

	const pageProps = { style: style, delFile: delFile, dictionary: dictionary, uploadFile: uploadFile, testValue: locale !== "ru" ? cookies : "a", openAuthModal: openAuthModal };

	const UploadFileScreenProps = { uploadedProgress: uploadedProgress, setUploadedProgress: setUploadedProgress, dictionary: dictionary, isTrim: isTrim, ...trimData };

	const pageClassName = !state?.previewImg && isTrim !== "wip" && !state.video ? "ai-main" : "remove_bg";
	const sectionClassName = !state?.previewImg && isTrim !== "wip" && !state.video ? "ai-section" : `section ${style.remover}`;

	const title = dictionary["page_bgrem_title"];
	const description = dictionary["page_bgrem_description"];

	const getCanonicalLink = (locale) => {
		const enList = ["en", "ja", "ar"];

		if (enList.includes(locale)) {
			return `https://bgrem.ai/remove-video-bg/`;
		} else {
			return `https://bgrem.ai/${locale}/remove-video-bg/`;
		}
	};

	return (
		<Layout mainClassName={pageClassName} cookies={locale !== "ru" ? cookies : null}>
			<OpenGraphTwitterCard {...{ title, description }} url={`${locale !== "en" ? "/" + locale : ""}${pathname}`} canonical={getCanonicalLink(locale)} />

			<section className={sectionClassName}>
				{!state?.previewImg && isTrim !== "wip" && <UploadFileLayout {...UploadFileScreenProps} {...pageProps} />}

				{state?.previewImg && isTrim !== "wip" && <DynamicVideoProcessing isNoPro={removePlanTarget === "free"} saveToLocalStorage={saveToLocalStorage} {...pageProps} />}

				{isTrim === "wip" && <DynamicTrimVideo delFile={delFile} changeTrimStatus={setIsTrim} trimReadyHandler={trimReadyHandler} dictionary={dictionary} />}

				{extensionModal && <DynamicExtensionModal dictionary={dictionary} closeModal={() => setExtensionModal(false)} />}
			</section>
			{showAuthModal && <DynamicAuthModal closeHandler={closeAuthModal} serviceName="Bgremover" testValue={locale !== "ru" ? cookies : null} />}
		</Layout>
	);
}

export async function getServerSideProps(context) {
	const cookies = context.req.cookies["a_b_test"] || null;

	return { props: { cookies } };
}
